import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import RtlButton from './RtlButton';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  getMany as getLeaveRequests,
  approveLeaveRequest,
  rejectLeaveRequest,
  type LeaveRequest,
  type LeaveRequestStatus,
  type LeaveRequestType,
} from '../data/leaveRequests';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import CustomTablePagination from './CustomTablePagination';
import DateRangeFilter from './DateRangeFilter';

const INITIAL_PAGE_SIZE = 15;

export default function LeaveRequestList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, direction } = useLanguage();
  const { user } = useAuth();

  const dialogs = useDialogs();
  const notifications = useNotification();

  const isGMOrBM = user?.role === 'GM' || user?.role === 'BM';
  const isGM = user?.role === 'GM';

  const isOwnRequest = React.useCallback(
    (row: LeaveRequest) => row.user_id === user?.id || row.user?.id === user?.id,
    [user?.id],
  );

  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = React.useState<{
    rows: LeaveRequest[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<LeaveRequestStatus | ''>(() => {
    const status = searchParams.get('status');
    if (status && ['pending', 'approved', 'rejected'].includes(status)) {
      return status as LeaveRequestStatus;
    }
    return isGMOrBM ? 'pending' : '';
  });
  const [typeFilter, setTypeFilter] = React.useState<LeaveRequestType | ''>(() => {
    const type = searchParams.get('type');
    if (type && ['normal', 'sick'].includes(type)) {
      return type as LeaveRequestType;
    }
    return '';
  });
  const [dateFrom, setDateFrom] = React.useState<string | null>(
    searchParams.get('start_date_from') || null,
  );
  const [dateTo, setDateTo] = React.useState<string | null>(
    searchParams.get('start_date_to') || null,
  );
  const [idFilter, setIdFilter] = React.useState<number | undefined>(() => {
    const id = searchParams.get('id');
    return id ? Number(id) : undefined;
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = React.useState<null | HTMLElement>(null);

  // Default column visibility - specify which columns to hide by default
  const defaultColumnVisibility: GridColumnVisibilityModel = {
    note: false,        // Hidden by default
    created_at: false,  // Hidden by default
  };

  // Column visibility state - load from localStorage or use defaults
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(() => {
    const saved = localStorage.getItem('leaveRequestListColumnVisibility');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultColumnVisibility;
      }
    }
    return defaultColumnVisibility;
  });

  // Save column visibility to localStorage whenever it changes
  const handleColumnVisibilityModelChange = React.useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem('leaveRequestListColumnVisibility', JSON.stringify(newModel));
    },
    [],
  );

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleStatusFilterChange = React.useCallback(
    (status: LeaveRequestStatus | '') => {
      setStatusFilter(status);

      if (status) {
        searchParams.set('status', status);
      } else {
        searchParams.delete('status');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleTypeFilterChange = React.useCallback(
    (type: LeaveRequestType | '') => {
      setTypeFilter(type);

      if (type) {
        searchParams.set('type', type);
      } else {
        searchParams.delete('type');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleDateFromChange = React.useCallback(
    (date: string | null) => {
      setDateFrom(date);

      if (date) {
        searchParams.set('start_date_from', date);
      } else {
        searchParams.delete('start_date_from');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleDateToChange = React.useCallback(
    (date: string | null) => {
      setDateTo(date);

      if (date) {
        searchParams.set('start_date_to', date);
      } else {
        searchParams.delete('start_date_to');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const listData = await getLeaveRequests({
        paginationModel,
        sortModel,
        filterModel,
        status: statusFilter || undefined,
        startDateFrom: dateFrom || undefined,
        startDateTo: dateTo || undefined,
        type: typeFilter || undefined,
        id: idFilter,
        sortBy: 'created_at',
        sortOrder: 'desc',
      });

      setRowsState({
        rows: listData.items,
        rowCount: listData.itemCount,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [paginationModel, sortModel, filterModel, statusFilter, typeFilter, dateFrom, dateTo, idFilter]);

  // Watch for changes in the id query parameter
  React.useEffect(() => {
    const id = searchParams.get('id');
    const newIdFilter = id ? Number(id) : undefined;

    // Only update if the value actually changed
    if (newIdFilter !== idFilter) {
      setIdFilter(newIdFilter);
    }
  }, [searchParams]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = React.useCallback(() => {
    if (!isLoading) {
      // Clear the id filter when refreshing
      if (idFilter) {
        setIdFilter(undefined);
        searchParams.delete('id');
        const newSearchParamsString = searchParams.toString();
        navigate(
          `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
          { replace: true }
        );
      } else {
        loadData();
      }
    }
  }, [isLoading, loadData, idFilter, searchParams, navigate, pathname]);

  const handleRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      // Could navigate to detail view if needed
      // navigate(`/leave-requests/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = React.useCallback(() => {
    navigate('/leave-requests/new');
  }, [navigate]);

  const handleColumnMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  }, []);

  const handleColumnMenuClose = React.useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleToggleColumn = React.useCallback(
    (field: string) => {
      setColumnVisibilityModel((prev) => {
        const newModel = {
          ...prev,
          [field]: prev[field] === false ? true : false,
        };
        localStorage.setItem('leaveRequestListColumnVisibility', JSON.stringify(newModel));
        return newModel;
      });
    },
    [],
  );

  // Define available columns for visibility toggle
  const availableColumns = React.useMemo(() => {
    const cols = [];
    if (isGMOrBM) {
      cols.push({ field: 'user', label: t('leaveRequests.employee') });
    }
    cols.push(
      { field: 'type', label: t('leaveRequests.type') },
      { field: 'start_date', label: t('leaveRequests.startDate') },
      { field: 'end_date', label: t('leaveRequests.endDate') },
      { field: 'days_number', label: t('leaveRequests.days') },
      { field: 'status', label: t('leaveRequests.status') },
      { field: 'note', label: t('leaveRequests.note') },
      { field: 'created_at', label: t('leaveRequests.createdAt') },
    );
    return cols;
  }, [t, isGMOrBM]);

  const handleApprove = React.useCallback(
    (leaveRequest: LeaveRequest) => async () => {
      // Prevent users from approving their own requests
      if (leaveRequest.user_id === user?.id || leaveRequest.user?.id === user?.id) {
        notifications.show(t('leaveRequests.cannotManageOwnRequest'), {
          severity: 'warning',
          autoHideDuration: 3000,
        });
        return;
      }
      const confirmed = await dialogs.confirm(
        t('leaveRequests.approveConfirm'),
        {
          title: t('leaveRequests.approve'),
          severity: 'info',
          okText: t('common.confirm'),
          cancelText: t('common.cancel'),
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          await approveLeaveRequest(leaveRequest.id);
          notifications.show(t('leaveRequests.approveSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (approveError) {
          notifications.show(
            `${t('messages.errorOccurred')}: ${(approveError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
        setIsLoading(false);
      }
    },
    [dialogs, notifications, loadData, t, user?.id],
  );

  const handleReject = React.useCallback(
    (leaveRequest: LeaveRequest) => async () => {
      // Prevent users from rejecting their own requests
      if (leaveRequest.user_id === user?.id || leaveRequest.user?.id === user?.id) {
        notifications.show(t('leaveRequests.cannotManageOwnRequest'), {
          severity: 'warning',
          autoHideDuration: 3000,
        });
        return;
      }
      const confirmed = await dialogs.confirm(
        t('leaveRequests.rejectConfirm'),
        {
          title: t('leaveRequests.reject'),
          severity: 'error',
          okText: t('common.confirm'),
          cancelText: t('common.cancel'),
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          await rejectLeaveRequest(leaveRequest.id);
          notifications.show(t('leaveRequests.rejectSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (rejectError) {
          notifications.show(
            `${t('messages.errorOccurred')}: ${(rejectError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
        setIsLoading(false);
      }
    },
    [dialogs, notifications, loadData, t, user?.id],
  );

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const isRtl = direction === 'rtl';

  const getStatusColor = (status: LeaveRequestStatus) => {
    switch (status) {
      case 'pending':
        return '#ff9800'; // Orange
      case 'approved':
        return '#4caf50'; // Green
      case 'rejected':
        return '#f44336'; // Red
      default:
        return '#757575'; // Gray
    }
  };

  const getTypeLabel = (type: LeaveRequestType) => {
    return type === 'normal' ? t('leaveRequests.normal') : t('leaveRequests.sick');
  };

  const columns = React.useMemo<GridColDef[]>(
    () => {
      const cols: GridColDef[] = [];
      
      if (isGMOrBM) {
        cols.push({
          field: 'user',
          headerName: t('leaveRequests.employee'),
          width: 120,
          minWidth: 100,
          flex: 0.8,
          valueGetter: (value: { name?: string } | null | undefined) => value?.name || '',
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        });
      }

      cols.push(
        {
          field: 'type',
          headerName: t('leaveRequests.type'),
          width: 90,
          minWidth: 80,
          flex: 0.6,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            return getTypeLabel(params.value);
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
        {
          field: 'start_date',
          headerName: t('leaveRequests.startDate'),
          width: 100,
          minWidth: 90,
          flex: 0.7,
          align: 'center',
          headerAlign: 'center',
          valueFormatter: (value) => {
            if (!value) return '';
            const date = new Date(value);
            return date.toLocaleDateString();
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
        {
          field: 'end_date',
          headerName: t('leaveRequests.endDate'),
          width: 100,
          minWidth: 90,
          flex: 0.7,
          align: 'center',
          headerAlign: 'center',
          valueFormatter: (value) => {
            if (!value) return '';
            const date = new Date(value);
            return date.toLocaleDateString();
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
        {
          field: 'days_number',
          headerName: t('leaveRequests.days'),
          type: 'number',
          width: 70,
          minWidth: 60,
          flex: 0.5,
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            // Try days_number first, then fall back to days
            return (row as any).days_number ?? (row as any).days ?? 0;
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
        {
          field: 'status',
          headerName: t('leaveRequests.status'),
          width: 110,
          minWidth: 100,
          flex: 0.8,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const status = params.value as LeaveRequestStatus;
            const color = getStatusColor(status);
            const row = params.row as LeaveRequest;
            
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  py: 1,
                  overflow: 'visible',
                }}
              >
                <Chip
                  label={t(`leaveRequests.${status}`)}
                  size="small"
                  sx={{
                    backgroundColor: color,
                    color: 'white',
                    fontWeight: 600,
                    minWidth: 90,
                  }}
                />
                {/* Mobile action buttons - visible on small and medium screens, hidden for own requests */}
                {isGMOrBM && !isOwnRequest(row) && (
                  <Box
                    sx={{
                      display: { xs: 'flex', lg: 'none' },
                      gap: 1,
                      justifyContent: 'center',
                      mt: 0.5,
                    }}
                  >
                    {status === 'rejected' && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(row)();
                        }}
                        sx={{
                          color: '#4caf50',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          },
                        }}
                        aria-label={t('leaveRequests.approve')}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    )}
                    {status === 'approved' && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(row)();
                        }}
                        sx={{
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                        aria-label={t('leaveRequests.reject')}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )}
                    {status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(row)();
                          }}
                          sx={{
                            color: '#4caf50',
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            },
                          }}
                          aria-label={t('leaveRequests.approve')}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReject(row)();
                          }}
                          sx={{
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            },
                          }}
                          aria-label={t('leaveRequests.reject')}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            );
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
        {
          field: 'note',
          headerName: t('leaveRequests.note'),
          width: 150,
          minWidth: 120,
          flex: 1,
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
        {
          field: 'created_at',
          headerName: t('leaveRequests.createdAt'),
          width: 100,
          minWidth: 90,
          flex: 0.7,
          align: 'center',
          headerAlign: 'center',
          valueFormatter: (value) => {
            if (!value) return '';
            const date = new Date(value);
            return date.toLocaleDateString();
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        },
      );
      
      if (isGMOrBM) {
        cols.push({
          field: 'actions',
          type: 'actions',
          width: 100,
          minWidth: 90,
          align: 'center',
          headerAlign: 'center',
          headerName: t('common.actions'),
          // Hide actions column on mobile (buttons shown in status column instead)
          hideable: false,
          getActions: ({ row }) => {
            // Users cannot approve/reject their own requests
            if (isOwnRequest(row as LeaveRequest)) {
              return [];
            }

            const actions = [];
            
            // If rejected, show approve option
            if (row.status === 'rejected') {
              actions.push(
                <GridActionsCellItem
                  key="approve-item"
                  icon={<CheckCircleIcon sx={{ color: '#4caf50' }} />}
                  label={t('leaveRequests.approve')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(row)();
                  }}
                />
              );
            }
            // If approved, show reject option
            else if (row.status === 'approved') {
              actions.push(
                <GridActionsCellItem
                  key="reject-item"
                  icon={<CancelIcon sx={{ color: '#f44336' }} />}
                  label={t('leaveRequests.reject')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject(row)();
                  }}
                />
              );
            }
            // If pending, show both options
            else if (row.status === 'pending') {
              actions.push(
                <GridActionsCellItem
                  key="approve-item"
                  icon={<CheckCircleIcon sx={{ color: '#4caf50' }} />}
                  label={t('leaveRequests.approve')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(row)();
                  }}
                />,
                <GridActionsCellItem
                  key="reject-item"
                  icon={<CancelIcon sx={{ color: '#f44336' }} />}
                  label={t('leaveRequests.reject')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject(row)();
                  }}
                />
              );
            }
            
            return actions;
          },
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          resizable: false,
        });
      }
      
      return cols;
    },
    [handleApprove, handleReject, t, isRtl, isGMOrBM, isOwnRequest, user?.id],
  );

  const pageTitle = t('leaveRequests.title');

  return (
    <PageContainer
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t('common.refresh')} placement="bottom" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleRefresh}
                sx={{ marginLeft: isRtl ? 1 : 0 }}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>

            <Tooltip title="Toggle Columns" placement="bottom" enterDelay={1000}>
                <IconButton
                    size="small"
                    aria-label="toggle columns"
                    onClick={handleColumnMenuOpen}
                >
                    <ViewColumnIcon />
                </IconButton>
            </Tooltip>
          {!isGM && (
            <RtlButton
              variant="contained"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
              sx={{
                background: '#028bcb !important',
                boxShadow: 'unset !important',
                border: '0 !important',
                '&:hover': { background: '#0277a8 !important' },
              }}
            >
              {t('common.create')}
            </RtlButton>
          )}
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {isGMOrBM && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
              px: 1,
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ right: isRtl ? 1 : 'auto', left: isRtl ? 'auto' : 0 }}>
                  {t('leaveRequests.status')}
              </InputLabel>
              <Select
                value={statusFilter}
                label={t('leaveRequests.status')}
                onChange={(e) => handleStatusFilterChange(e.target.value as LeaveRequestStatus | '')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="pending">{t('leaveRequests.pending')}</MenuItem>
                <MenuItem value="approved">{t('leaveRequests.approved')}</MenuItem>
                <MenuItem value="rejected">{t('leaveRequests.rejected')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ right: isRtl ? 1 : 'auto', left: isRtl ? 'auto' : 0 }}>
                  {t('leaveRequests.type')}
              </InputLabel>
              <Select
                value={typeFilter}
                label={t('leaveRequests.type')}
                onChange={(e) => handleTypeFilterChange(e.target.value as LeaveRequestType | '')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="normal">{t('leaveRequests.normal')}</MenuItem>
                <MenuItem value="sick">{t('leaveRequests.sick')}</MenuItem>
              </Select>
            </FormControl>
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={handleDateFromChange}
              onDateToChange={handleDateToChange}
            />
          </Box>
        )}
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
              <DataGrid
                rows={rowsState.rows}
                rowCount={rowsState.rowCount}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                onRowClick={handleRowClick}
                loading={isLoading}
                initialState={initialState}
                pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25, 50]}
                hideFooter
                localeText={{
                  noRowsLabel: t('dataGrid.noRowsLabel'),
                  noResultsOverlayLabel: t('dataGrid.noResultsOverlayLabel'),
                }}
                slotProps={{
                  loadingOverlay: {
                    variant: 'circular-progress',
                    noRowsVariant: 'circular-progress',
                  },
                  baseIconButton: {
                    size: 'small',
                  },
                }}
                sx={(theme) => ({
                  height: '100%',
                  width: '100%',
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                    {
                      outline: 'none',
                    },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  },
                  [`& .${gridClasses.columnSeparator}`]: {
                    display: 'none',
                  },
                  [`& .${gridClasses.columnHeader}`]: {
                    cursor: 'default !important',
                  },
                  // Hide actions column on mobile and tablet (below lg breakpoint)
                  [theme.breakpoints.down('lg')]: {
                    [`& .${gridClasses.columnHeader}[data-field="actions"], & .${gridClasses.cell}[data-field="actions"]`]: {
                      display: 'none !important',
                    },
                  },
                })}
              />
            </Box>
            <CustomTablePagination
              paginationModel={paginationModel}
              rowCount={rowsState.rowCount}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25, 50]}
            />
          </Box>
        )}
      </Box>

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'rtl' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'rtl' ? 'left' : 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
          <Stack spacing={0.5}>
            {availableColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={columnVisibilityModel[col.field] !== false}
                    onChange={() => handleToggleColumn(col.field)}
                    size="small"
                  />
                }
                label={col.label}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </PageContainer>
  );
}

